import type { FolderNode } from "@/action-dialog/dialogs/folderTree";
import type { Folder } from "@cohesive/api-client";

/**
 * Canvas folders form a real parent/child tree in the `folders` table, so the
 * destination picker identifies them by id. `FolderNode.path` carries that id
 * — the field is the node's key, and for file folders that key happens to be
 * a path. Keeping one node shape lets both trees share the picker.
 *
 * A folder whose parent is missing from the input surfaces at the root rather
 * than disappearing, matching the file-folder builder.
 */
export function buildCanvasFolderTree(folders: Folder[]): FolderNode[] {
  const byId = new Map<string, FolderNode>();
  for (const folder of folders) {
    byId.set(folder.id, { path: folder.id, name: folder.name, children: [] });
  }

  const roots: FolderNode[] = [];
  for (const folder of folders) {
    const node = byId.get(folder.id);
    if (!node) continue;
    const parent = folder.parent_folder_id
      ? byId.get(folder.parent_folder_id)
      : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const sortTree = (nodes: FolderNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const node of nodes) sortTree(node.children);
  };
  sortTree(roots);

  return roots;
}

/**
 * The folder plus everything beneath it — the destinations a folder move must
 * refuse, since a folder cannot become its own descendant.
 */
export function folderSubtreeIds(
  folders: Folder[],
  rootId: string,
): ReadonlySet<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const folder of folders) {
    if (!folder.parent_folder_id) continue;
    const siblings = childrenByParent.get(folder.parent_folder_id) ?? [];
    siblings.push(folder.id);
    childrenByParent.set(folder.parent_folder_id, siblings);
  }

  const subtree = new Set<string>();
  const walk = (id: string) => {
    if (subtree.has(id)) return;
    subtree.add(id);
    for (const child of childrenByParent.get(id) ?? []) walk(child);
  };
  walk(rootId);

  return subtree;
}
